import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { formaterAvecUnite, formaterHeure, PALIERS_PUISSANCE } from '../../Services/format';

export type QuickMetricItem = {
  icon: string;
  title: string;
  value: string;
  /** Indique que la valeur est encore statique/fictive (affichage en mode maquette). */
  maquette?: boolean;
  tooltip?: string;
};

/**
 * Formate une puissance crête (kWc) en chaîne lisible en français.
 * - Passe en MWc au-delà de 1000 kWc.
 * - 1 décimale si la valeur affichée est < 10, arrondi à l'entier sinon.
 * - La décimale n'est pas affichée si elle vaut zéro (ex. 5 → "5 kWc").
 */
export function formatPuissanceCrete(pc: number): string {
  const fmt = (n: number) =>
    n.toLocaleString('fr-FR', {
      maximumFractionDigits: n < 10 ? 1 : 0,
      minimumFractionDigits: 0,
    });
  if (pc >= 1000) return `${fmt(pc / 1000)} MWc`;
  return `${fmt(pc)} kWc`;
}

/** Formate une production instantanée fournie en watts par l'API. */
export function formatProductionInstantanee(value: number | null | undefined): string {
  return formaterAvecUnite(value, PALIERS_PUISSANCE);
}

export function tooltipProductionInstantanee(
  date: string | null | undefined,
  value: number | null | undefined,
): string {
  if (value == null || !Number.isFinite(value)) return 'Aucune production instantanée relevée';
  if (!date || Number.isNaN(new Date(date).getTime())) return 'Heure de relève indisponible';
  return `Production instantannée relevée à ${formaterHeure(date)}`;
}

type QuickMetricTemplate = Omit<QuickMetricItem, 'value'>;

export const QM_PUISSANCE_CRETE: QuickMetricTemplate = {
  icon: 'solar_power',
  title: 'Puissance crête',
};
export const QM_PRODUCTION: QuickMetricTemplate = {
  icon: 'query_stats',
  title: 'Production instantanée',
};
export const QM_PERTE: QuickMetricTemplate = { icon: 'euro', title: 'Perte' };

@Component({
  selector: 'app-quick-metrics-grid',
  imports: [CommonModule],
  templateUrl: './quick-metrics-grid.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './quick-metrics-grid.css',
})
export class QuickMetricsGridComponent {
  @Input() items: QuickMetricItem[] = [];
  @Input() avecContour = true;
}
